import {
  CLUSTERS_FIELDS,
  PIN_BORDER_WIDTH_ACTIVE_WITH_BALLOON,
  PIN_BORDER_WIDTH_CLICKABLE,
  PIN_BORDER_WIDTH_REGULAR,
  PIN_BORDER_WIDTH_WITH_BALLOON,
  PIN_RADIUS_ACTIVE_WITH_BALLOON,
  PIN_RADIUS_CLICKABLE,
  PIN_RADIUS_CLICKABLE_WITH_COUNT,
  PIN_RADIUS_REGULAR,
  PIN_RADIUS_WITH_BALLOON,
} from 'shared/constants/map';
import { parseClusterCount } from 'shared/utils/map/parseClusterCount';
import {
  PIN_EXTENDED_TYPES,
  PIN_TYPES,
  PinExtendedType,
  PinExtendedTypes,
  PinType,
} from 'shared/utils/mapModules/PinsLayer/types';

import { ColorsConfig, IPinService, PinServiceInitOptions, SizesConfig } from '../IPinService';

import { COLORS_CONFIG_MAP_WITHOUT_BALLOON, COLORS_CONFIG_MAP_WITH_BALLOON } from './constants';

export class PinService implements IPinService {
  private cluster;

  public constructor(parameters: PinServiceInitOptions) {
    this.cluster = parameters.cluster;
  }

  public provideId = (): string => {
    return this.cluster.id;
  };

  public provideListingId = (): string | undefined => {
    return this.cluster.properties?.[CLUSTERS_FIELDS.listingId];
  };

  public get isPinInteractive(): boolean {
    return !!this.provideListingId();
  }

  public provideColors = (): ColorsConfig => {
    const pinType = this.provideType();
    const extendedTypes = this.provideExtendedTypes();
    const withBalloon = extendedTypes.has(PIN_EXTENDED_TYPES.withBalloon);
    const isActive = extendedTypes.has(PIN_EXTENDED_TYPES.active);
    const isViewed = extendedTypes.has(PIN_EXTENDED_TYPES.viewed);

    if (withBalloon) {
      const config = COLORS_CONFIG_MAP_WITH_BALLOON[pinType];

      if (isActive) {
        return config[PIN_EXTENDED_TYPES.active];
      }

      if (isViewed) {
        return config[PIN_EXTENDED_TYPES.viewed];
      }

      return config.default;
    }

    const config = COLORS_CONFIG_MAP_WITHOUT_BALLOON[pinType];

    /**
     * "isActive" для пина без балуна не принимаем во внимание, т.к. активное
     * состояние такого пина - это разметка поверх пина, стили самого пина в этом
     * случае не важны
     */
    if (isViewed) {
      return config[PIN_EXTENDED_TYPES.viewed];
    }

    return config.default;
  };

  public provideCount = (): string => {
    return this.cluster.properties?.[CLUSTERS_FIELDS.count] || '';
  };

  public provideIsWithShadow = (): boolean => {
    const extendedTypes = this.provideExtendedTypes();
    const withBalloon = extendedTypes.has(PIN_EXTENDED_TYPES.withBalloon);

    if (withBalloon) {
      return false;
    }

    return this.isPinInteractive;
  };

  public provideSizes = (): SizesConfig => {
    const sizes = { borderWidth: PIN_BORDER_WIDTH_REGULAR, radius: PIN_RADIUS_REGULAR };
    const extendedTypes = this.provideExtendedTypes();
    const withBalloon = extendedTypes.has(PIN_EXTENDED_TYPES.withBalloon);
    const isActive = extendedTypes.has(PIN_EXTENDED_TYPES.active);

    if (withBalloon) {
      sizes.radius = PIN_RADIUS_WITH_BALLOON;
      sizes.borderWidth = PIN_BORDER_WIDTH_WITH_BALLOON;

      if (isActive) {
        sizes.radius = PIN_RADIUS_ACTIVE_WITH_BALLOON;
        sizes.borderWidth = PIN_BORDER_WIDTH_ACTIVE_WITH_BALLOON;
      }

      return sizes;
    }

    if (this.isPinInteractive) {
      sizes.radius = PIN_RADIUS_CLICKABLE;
      sizes.borderWidth = PIN_BORDER_WIDTH_CLICKABLE;
    }

    const count = this.parseClusterCount();

    if (count) {
      sizes.radius = PIN_RADIUS_CLICKABLE_WITH_COUNT;
      sizes.borderWidth = PIN_BORDER_WIDTH_CLICKABLE;
    }

    return sizes;
  };

  public provideTitle = (): string | undefined => {
    return this.cluster.properties?.[CLUSTERS_FIELDS.title];
  };

  public provideType = (): PinType => {
    if (this.cluster.properties?.[CLUSTERS_FIELDS.offGeo]) {
      return PIN_TYPES.regularOutsideGeo;
    }

    return PIN_TYPES.regular;
  };

  public provideExtendedTypes = (): PinExtendedTypes => {
    const { properties } = this.cluster;

    const pinExtendedTypes = new Set<PinExtendedType>();

    if (properties) {
      if (properties[CLUSTERS_FIELDS.count]) {
        pinExtendedTypes.add(PIN_EXTENDED_TYPES.withCount);
      }

      if (properties[CLUSTERS_FIELDS.isActive]) {
        pinExtendedTypes.add(PIN_EXTENDED_TYPES.active);
      }

      if (properties[CLUSTERS_FIELDS.isFavorite]) {
        pinExtendedTypes.add(PIN_EXTENDED_TYPES.favorite);
      }

      if (properties[CLUSTERS_FIELDS.isViewed]) {
        pinExtendedTypes.add(PIN_EXTENDED_TYPES.viewed);
      }

      if (properties[CLUSTERS_FIELDS.title]) {
        pinExtendedTypes.add(PIN_EXTENDED_TYPES.withBalloon);
      }
    }

    return pinExtendedTypes;
  };

  private parseClusterCount = (): number | undefined => {
    return parseClusterCount(this.cluster);
  };
}
